import { createRoute, redirect } from '@tanstack/react-router';
import { lazyPage } from '../common';
import { siteRoute } from '../layouts';

const pages = () => import('./pages');
const layoutComp = lazyPage(pages, 'AnalyticsLayout');
const dashboardComp = lazyPage(pages, 'AnalyticsDashboardHost');
const chatComp = lazyPage(pages, 'AnalyticsChatHost');
const reportComp = lazyPage(pages, 'AnalyticsReportHost');
const customComp = lazyPage(pages, 'AnalyticsCustomReportHost');

/** The analytics chat seeds its conversation from the `?q=` search param. */
const analyticsChatSearch = (search: Record<string, unknown>) => ({
  q:
    typeof search.q === 'string'
      ? search.q
      : typeof search.q === 'number'
        ? String(search.q)
        : undefined,
});

// The nested analytics layout (upgrade gating + report nav). The leaves are full-bleed/full-height
// so `AnalyticsLayout` owns the rail, the scroll container and the page padding. `remountDeps` on
// the report/custom leaves remounts them when the slug changes.
const analyticsLayoutRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'analytics',
  component: layoutComp,
  staticData: { title: 'Analytics' },
});

// /analytics redirects to the dashboard.
const analyticsIndexRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: '/',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/analytics/dashboard', params });
  },
});

const analyticsDashboardRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: 'dashboard',
  component: dashboardComp,
  staticData: { title: 'Analytics', fullBleed: true, fullHeight: true },
});

const analyticsChatRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: 'chat',
  component: chatComp,
  validateSearch: analyticsChatSearch,
  staticData: { title: 'Analytics Chat', fullBleed: true, fullHeight: true },
});

const analyticsReportRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: 'report/$reportSlug',
  component: reportComp,
  remountDeps: ({ params }) => params.reportSlug as string,
  staticData: { title: 'Analytics Report', fullBleed: true, fullHeight: true },
});

const analyticsCustomRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: 'custom/$customId',
  component: customComp,
  remountDeps: ({ params }) => params.customId as string,
  staticData: { title: 'Analytics Custom Report', fullBleed: true, fullHeight: true },
});

/** Analytics family - assemble the nested layout (the layout route is the child of the site route). */
export const analyticsTree = analyticsLayoutRoute.addChildren([
  analyticsIndexRoute,
  analyticsDashboardRoute,
  analyticsChatRoute,
  analyticsReportRoute,
  analyticsCustomRoute,
]);
